import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, moment, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles, useWindowDimensions } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type ErrorChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  exportTitle?: string;
  exportSubTitle?: string;
  showError?: boolean;
  setExternalEmbed?: (_json: types.analysis.EmbedChartData) => void;
  yAxisLabel?: string;
  xAxisLabel?: string;
  xAxisField?: string;
  yAxisField?: string;
  percent?: boolean;
  bucket?: string;
  showBucketOnTooltip?: boolean;
  download?: boolean;
  formatData?: (_data: types.widgets.ErrorLinearData) => types.widgets.ErrorLinearData;
};

/**
 * Error chart component that displays data in a error format
 *
 * @param id - Unique identifier for the chart
 * @param data - Data to be displayed in the chart
 * @param fullScreen - Whether the chart is in fullscreen mode
 * @param readOnly - Whether the chart is in read-only mode
 * @param alert - Alert message to display above the chart
 * @param exportTitle - Title used when exporting the chart
 * @param exportSubTitle - Subtitle used when exporting the chart
 * @param showError - Whether to show error indicators
 * @param setExternalEmbed - Callback to set external embed data
 * @param yAxisLabel - Label for y-axis
 * @param xAxisLabel - Label for x-axis
 * @param xAxisField - Field to use for x-axis values. Default is 'date' for time series
 * @param yAxisField - Field to use for y-axis values. Default is 'value'
 * @param percent - Whether to display values as percentages. Default is true
 * @param bucket - Bucket identifier for data grouping
 * @param showBucketOnTooltip - Whether to show bucket info on tooltip
 * @param download - Whether to show download button
 * @returns React component displaying an error chart
 */
const ErrorChart = (args: ErrorChartProps) => {
  const {
    id,
    data,
    fullScreen,
    readOnly,

    alert,
    exportTitle,
    exportSubTitle,
    setExternalEmbed,
    yAxisLabel,
    xAxisLabel,
    xAxisField = 'date',
    yAxisField = 'value',
    percent = true,
    bucket = undefined,
    showBucketOnTooltip = false,
    download = true,
    showError = true,
    formatData = undefined,
  } = args;

  const uiStyles = useUiStyles();
  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  const { width } = useWindowDimensions(1_000);

  const isDate = xAxisField === 'date';

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'error-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      if (isDate) {
        setLocalData(
          data.map((row) => ({
            ...row,
            date: moment(row.date as string).valueOf() as number,
          }))
        );
      } else {
        setLocalData(data);
      }
    };

    handleData();
  }, [data, width]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}

      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              fullScreen={fullScreen}
              xAxisField={xAxisField}
              yAxisField={yAxisField}
              percent={percent}
              bucket={bucket}
              showBucketOnTooltip={showBucketOnTooltip}
              yAxisLabel={yAxisLabel}
              xAxisLabel={xAxisLabel}
              download={download}
              showError={showError}
              formatData={formatData}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default ErrorChart;
